import * as React from "react";
import { useState, useEffect } from "react";
import { TAGNAME_CHAR_LIMIT } from "sh-application/../StaffHubConstants";
import { setHoverColumnHidden } from "sh-application/store";
import AutomationUtils from "sh-application/utility/AutomationUtil";
import InstrumentationUtils from "sh-application/utility/InstrumentationUtils";
import { ConfirmDialog } from "sh-confirm-dialog/lib/components/ConfirmDialog";
import { getGenericEventPropertiesObject } from "sh-instrumentation";
import { ITagEntity, ScheduleCalendarType } from "sh-models";
import {
    ECSConfigKey,
    ECSConfigService,
    InstrumentationService
} from "sh-services";
import StringsStore from "sh-strings/store";
import type { InputOnChangeData } from "@fluentui/react-components";
import { FluentProvider, Input } from "@fluentui/react-components";
import { TagHeaderViewStateStore } from "./lib/store/schema/TagHeaderViewStateStoreSchema";
import schedulesViewStateStore from "../../schedules/lib/store/store";
import ThemeUtils from "../../../utility/ThemeUtils";

const styles = require("./RenameGroupDialog.scss");
const classNames = require("classnames/bind");

type RenameGroupDialogProps = {
    oldTagName: string;
    tagName: string;
    saveTagName: (newName: string) => void;
    setTagHeaderViewState: (newState: (prevState: TagHeaderViewStateStore) => TagHeaderViewStateStore) => void;
    setFocusOnMoreMenuButton: () => void;
    setIsConfirmDialogVisible: (isVisible: boolean) => void;
    editEnabled: boolean;
    isNotFocusable: boolean;
    tag: ITagEntity | null;
    scheduleCalendarType: ScheduleCalendarType;
};

export function RenameGroupDialog(props: RenameGroupDialogProps) {

    const [tagName, setTagName] = useState(props.tagName);

    const strings = StringsStore().registeredStringModules.get("schedulePage").strings;
    const commonStrings = StringsStore().registeredStringModules.get("common").strings;

    useEffect(() => {
        setHoverColumnHidden(schedulesViewStateStore(), true);
    }, []);

    function onTagNameTextFieldChanged(ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) {
        setTagName(data.value);
        handleTagHeaderNameChange(data.value);
    }

    function handleTagHeaderNameChange(tagName: string) {
        props.setTagHeaderViewState((prevState) => {
            return {
                ...prevState,
                tagName: tagName
            };
        });
    }

    /**
     * Renders the group name text field for editing the group name
     */
    function renderGroupNameTextFieldInModal() {
        const viewState = props;
        const tabIndex = viewState.isNotFocusable ? -1 : 0;
        return (
            <FluentProvider theme={ThemeUtils.getFluentV2ShiftTheme()}>
                <Input
                    className={ classNames(AutomationUtils.getAutomationId("group", "QAIDGroupNameTextField"), styles.groupRenameInputInModal) }
                    data-testid="groupRenameInputInModalTestId"
                    tabIndex={ tabIndex }
                    placeholder={ viewState.editEnabled ? strings.get("enterGroupName") : "" }
                    aria-label={ strings.get("groupNameFieldAriaLabel") }
                    value = { tagName }
                    disabled={ viewState.tag === null || !viewState.editEnabled || viewState.isNotFocusable }  // The default tag's name cannot be edited.
                    maxLength={ TAGNAME_CHAR_LIMIT }
                    onChange={ onTagNameTextFieldChanged } />
                { ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableShiftsTargetingHelperText) && <div className={ styles.renameGroupHelperText }>{ strings.get("renameGroupHelperText") }</div> }
            </FluentProvider>
        );
    }

    return (
            <ConfirmDialog
                title={ strings.get("renameGroupLabel") }
                customizationOptions={
                    {
                        bodyElement: (renderGroupNameTextFieldInModal()),
                        isBlocking: true,
                        containerClassName: classNames(AutomationUtils.getAutomationId("confirm", "QAIDConfirmDialog"), styles.confirmDialog),
                        okText: commonStrings.get("save")
                    }
                }
                onOkCallback={ () => {
                    props.saveTagName(tagName);
                    if (props.tag) {
                        window.sessionStorage.removeItem(`tag-${props.tag.id}`);
                    }
                    InstrumentationService.logEvent(InstrumentationService.events.RenameDialogOkClicked,
                        [getGenericEventPropertiesObject(InstrumentationService.properties.CurrentView,
                            InstrumentationUtils.getCurrentViewForInstrumentation(props.scheduleCalendarType))]);
                }}
                onCancelCallback={ () => {
                    // Reset the tag name to the original name
                    handleTagHeaderNameChange(props.oldTagName);
                    InstrumentationService.logEvent(InstrumentationService.events.RenameDialogCancelClicked,
                        [getGenericEventPropertiesObject(InstrumentationService.properties.CurrentView,
                            InstrumentationUtils.getCurrentViewForInstrumentation(props.scheduleCalendarType))]);
                }}
                onClose={ () => {
                    handleTagHeaderNameChange(props.oldTagName);
                    props.setIsConfirmDialogVisible(false);
                    props.setFocusOnMoreMenuButton();
                    if (!ECSConfigService.isECSFeatureEnabled(ECSConfigKey.NewAddGroupExperience)) {
                        setHoverColumnHidden(schedulesViewStateStore(), false);
                    }
                }}
                okButtonDisabled={ tagName.trim().length === 0 || tagName.trim() === props.oldTagName }
                showCloseButton={ true }
                callCancelOnDismiss={ true }
            />
        );
}